import type {
  VeeraContextualThreadEntrySurface,
  VeeraContextualThreadInput,
} from "@/lib/veeraContextualThreads";
import { buildVeeraContextualTopicId } from "@/lib/veeraContextualThreads";
import {
  buildVeeraThreadOpener,
  type VeeraThreadOpenerEvidenceStrength,
  type VeeraThreadOpenerResult,
} from "@/lib/veeraThreadOpeners";
import { buildVeeraThreadTitle } from "@/lib/veeraThreadTitles";
import {
  parseVeeraContextualEntryTransport,
  serializeVeeraContextualEntryTransport,
  type CoachVeeraContextualEntryTransport,
} from "../../supabase/functions/_shared/veeraContextualEntry.ts";

export type CoachVeeraContextualEntrySurface = Extract<
  VeeraContextualThreadEntrySurface,
  "leader_card" | "portfolio" | "post_meeting_review"
>;

export type CoachVeeraContextType = "global" | "leader" | "coach";

export interface CoachVeeraContextualEntryInput {
  surface: CoachVeeraContextualEntrySurface;
  coachId: string;
  contextType: CoachVeeraContextType;
  leaderId?: string | null;
  leaderName?: string | null;
  primaryEntityId: string;
  topicTitle: string;
  topicFingerprint: string;
  sourceLabel?: string | null;
  sourceId?: string | null;
  carryOverSummary: string;
  whyItMattersNow: string;
  evidenceStrength: VeeraThreadOpenerEvidenceStrength;
  question?: string | null;
}

export type CoachVeeraContextualEntry = CoachVeeraContextualEntryTransport & {
  contextualThreadInput: VeeraContextualThreadInput;
  assistantOpener: VeeraThreadOpenerResult;
};

function normalizeText(value: string | null | undefined, fallback = "") {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized ? normalized : fallback;
}

function normalizeNullableText(value: string | null | undefined) {
  const normalized = normalizeText(value);
  return normalized || null;
}

function normalizeTopicFingerprint(value: string | null | undefined) {
  const normalized = value?.replace(/\s+/g, " ").trim().toLowerCase();
  if (!normalized) return "unknown";
  return normalized
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "unknown";
}

function stripLeaderPrefixFromTitle(threadTitle: string, leaderName: string | null) {
  if (!leaderName) return threadTitle;
  const prefix = `${leaderName}:`;
  return threadTitle.startsWith(prefix)
    ? threadTitle.slice(prefix.length).trim()
    : threadTitle;
}

function normalizeSourceLabel(surface: CoachVeeraContextualEntrySurface, value?: string | null) {
  const normalized = normalizeText(value);
  if (normalized) return normalized;

  switch (surface) {
    case "leader_card":
      return "From Leaders";
    case "portfolio":
      return "From Portfolio";
    case "post_meeting_review":
      return "From Post-Meeting Review";
  }
}

export function buildCoachVeeraContextualEntry(
  params: CoachVeeraContextualEntryInput,
): CoachVeeraContextualEntry {
  const leaderName = normalizeNullableText(params.leaderName);
  const threadTitle = buildVeeraThreadTitle({
    role: "coach",
    entrySurface: params.surface,
    leaderName,
    topicTitle: normalizeText(params.topicTitle, "Coach Veera"),
  });
  const sourceLabel = normalizeSourceLabel(params.surface, params.sourceLabel);
  const topicFingerprint = normalizeTopicFingerprint(params.topicFingerprint);
  const primaryEntityId = normalizeText(params.primaryEntityId, params.coachId);
  const contextualTopicId = buildVeeraContextualTopicId({
    role: "coach",
    entrySurface: params.surface,
    primaryEntityId,
    topicFingerprint,
  });

  return {
    role: "coach",
    surface: params.surface,
    contextType: params.contextType,
    leaderId: normalizeNullableText(params.leaderId),
    leaderName,
    threadTitle,
    sourceLabel,
    topicFingerprint,
    contextualThreadInput: {
      role: "coach",
      entrySurface: params.surface,
      primaryEntityId,
      topicFingerprint,
      contextualTopicId,
      sourceId: normalizeNullableText(params.sourceId) ?? undefined,
    },
    assistantOpener: buildVeeraThreadOpener({
      role: "coach",
      entrySurface: params.surface,
      sourceLabel,
      carryOverSummary: params.carryOverSummary,
      whyItMattersNow: params.whyItMattersNow,
      evidenceStrength: params.evidenceStrength,
      question: params.question,
    }),
  };
}

export function serializeCoachVeeraContextualEntry(entry: CoachVeeraContextualEntry) {
  return serializeVeeraContextualEntryTransport(entry);
}

function isSurface(value: unknown): value is CoachVeeraContextualEntrySurface {
  return value === "leader_card" || value === "portfolio" || value === "post_meeting_review";
}

function isContextType(value: unknown): value is CoachVeeraContextType {
  return value === "global" || value === "leader" || value === "coach";
}

export function parseCoachVeeraContextualEntry(
  raw: string | null | undefined,
): CoachVeeraContextualEntry | null {
  if (!raw) return null;

  try {
    const parsed = parseVeeraContextualEntryTransport(raw);
    if (!parsed || parsed.role !== "coach" || !isSurface(parsed.surface) || !isContextType(parsed.contextType)) {
      return null;
    }

    const leaderName = normalizeNullableText(parsed.leaderName);
    const threadTitle = normalizeText(parsed.threadTitle ?? parsed.topicFingerprint, "Coach Veera");

    return buildCoachVeeraContextualEntry({
      surface: parsed.surface,
      coachId: normalizeText(parsed.contextualThreadInput?.primaryEntityId, "coach"),
      contextType: parsed.contextType,
      leaderId: normalizeNullableText(parsed.leaderId),
      leaderName,
      primaryEntityId: normalizeText(parsed.contextualThreadInput?.primaryEntityId, "coach"),
      topicTitle: stripLeaderPrefixFromTitle(threadTitle, leaderName),
      topicFingerprint: normalizeText(parsed.topicFingerprint ?? parsed.contextualThreadInput?.topicFingerprint, "unknown"),
      sourceLabel: normalizeNullableText(parsed.sourceLabel),
      sourceId: normalizeNullableText(parsed.contextualThreadInput?.sourceId),
      carryOverSummary: normalizeText(parsed.assistantOpener?.carryOverSummary),
      whyItMattersNow: normalizeText(parsed.assistantOpener?.whyItMattersNow),
      evidenceStrength: parsed.assistantOpener?.shouldAskQuestion ? "strong" : "thin",
      question: normalizeNullableText(parsed.assistantOpener?.question),
    });
  } catch {
    return null;
  }
}
